<!-- ============================================================
     TRANSPORT MANAGEMENT
     Tabs: Overview · Routes · Vehicles · Drivers · Allocations
     ============================================================ -->
<div class="tr-page">

  <!-- Toast -->
  @if (saveSuccess()) {
    <div class="tr-toast alert alert-success d-flex align-items-center gap-2 shadow">
      <i class="bi bi-check-circle-fill fs-5"></i><strong>{{ saveSuccess() }}</strong>
    </div>
  }

  <!-- Delete Modal -->
  @if (showDeleteModal()) {
    <div class="tr-overlay" (click)="showDeleteModal.set(false)">
      <div class="tr-modal tr-modal-sm" (click)="$event.stopPropagation()">
        <div class="trm-header bg-danger"><div class="trm-icon"><i class="bi bi-trash3-fill"></i></div>
          <div><h5 class="trm-title">Delete Record?</h5><p class="trm-sub">{{ deleteCtx()?.label }}</p></div>
          <button class="trm-close" (click)="showDeleteModal.set(false)"><i class="bi bi-x-lg"></i></button></div>
        <div class="trm-body p-4 text-center"><p class="text-muted small mb-0">This action cannot be undone.</p></div>
        <div class="trm-footer"><button class="btn btn-outline-secondary px-4" (click)="showDeleteModal.set(false)">Cancel</button>
          <button class="btn btn-danger px-4" (click)="executeDelete()"><i class="bi bi-trash3 me-2"></i>Delete</button></div>
      </div>
    </div>
  }

  <!-- ===================== PAGE HEADER ===================== -->
  <div class="tr-ph mb-4">
    <div>
      <h1 class="tr-title"><i class="bi bi-bus-front-fill me-2 text-primary" style="font-size:1.1rem;"></i>Transport Management</h1>
      <p class="tr-subtitle">Routes · Fleet · Drivers · Allocations · Daily Attendance</p>
    </div>
    <div class="d-flex gap-2 flex-wrap">
      @if (expiryAlerts().length > 0) {
        <div class="expiry-warning-chip"><i class="bi bi-exclamation-triangle-fill me-1"></i>{{ expiryAlerts().length }} document(s) expiring</div>
      }
      @if (activeTab() === 'routes' && routeMode() === 'list') {
        <button class="btn tr-btn-primary btn-sm" (click)="openNewRoute()"><i class="bi bi-plus-circle-fill me-1"></i>Add Route</button>
      }
      @if (activeTab() === 'vehicles' && vehicleMode() === 'list') {
        <button class="btn tr-btn-primary btn-sm" (click)="openNewVehicle()"><i class="bi bi-plus-circle-fill me-1"></i>Add Vehicle</button>
      }
      @if (activeTab() === 'drivers' && driverMode() === 'list') {
        <button class="btn tr-btn-primary btn-sm" (click)="openNewDriver()"><i class="bi bi-plus-circle-fill me-1"></i>Add Driver</button>
      }
      @if (activeTab() === 'allocations' && allocMode() === 'list') {
        <button class="btn tr-btn-primary btn-sm" (click)="openNewAlloc()"><i class="bi bi-plus-circle-fill me-1"></i>Allocate Student</button>
      }
      @if (activeTab() !== 'overview' && activeTab() !== 'attendance' && (routeMode() !== 'list' || vehicleMode() !== 'list' || driverMode() !== 'list' || allocMode() !== 'list')) {
        <button class="btn btn-outline-secondary btn-sm" (click)="routeMode.set('list'); vehicleMode.set('list'); driverMode.set('list'); allocMode.set('list')">
          <i class="bi bi-arrow-left me-1"></i>Back
        </button>
      }
    </div>
  </div>

  <!-- TABS -->
  <div class="tr-tabs mb-4">
    @for (t of [{k:'overview',l:'Overview',ic:'bi-grid-fill'},{k:'routes',l:'Routes',ic:'bi-map-fill'},{k:'vehicles',l:'Vehicles',ic:'bi-bus-front-fill'},{k:'drivers',l:'Drivers',ic:'bi-person-badge-fill'},{k:'allocations',l:'Allocations',ic:'bi-people-fill'},{k:'attendance',l:'Attendance',ic:'bi-calendar-check-fill'}]; track t.k) {
      <button class="trtab" [class.active]="activeTab() === t.k"
        (click)="activeTab.set($any(t.k)); setAttTab($any(t.k)); routeMode.set('list'); vehicleMode.set('list'); driverMode.set('list'); allocMode.set('list')">
        <i class="bi {{ t.ic }} me-1"></i>{{ t.l }}
      </button>
    }
  </div>

  <!-- ===================== OVERVIEW ===================== -->
  @if (activeTab() === 'overview') {
    <!-- Stats Grid -->
    <div class="ov-stats-grid mb-4">
      <div class="ov-stat gap-3" [class.stat-warn]="stats().activeRoutes < stats().routes">
        <div class="ovs-icon"><i class="bi bi-map-fill"></i></div>
        <div class="pt-2">
          <div class="ovs-main"><span class="ovs-val">{{ stats().activeRoutes }}</span><span class="ovs-tot">/{{ stats().routes }}</span></div>
          <div class="ovs-label">Active Routes</div>
        </div>
      </div>
      <div class="ov-stat gap-3" [class.stat-warn]="stats().maintenance > 0">
        <div class="ovs-icon" style="background:linear-gradient(135deg,#0284c7,#38bdf8)"><i class="bi bi-bus-front-fill"></i></div>
        <div>
          <div class="ovs-main"><span class="ovs-val">{{ stats().activeVehicles }}</span><span class="ovs-tot">/{{ stats().vehicles }}</span></div>
          <div class="ovs-label">Active Vehicles <span class="ms-1 small text-warning">{{ stats().maintenance > 0 ? '· ' + stats().maintenance + ' in service' : '' }}</span></div>
        </div>
      </div>
      <div class="ov-stat gap-3">
        <div class="ovs-icon" style="background:linear-gradient(135deg,#059669,#10b981)"><i class="bi bi-person-badge-fill"></i></div>
        <div>
          <div class="ovs-main"><span class="ovs-val">{{ stats().activeDrivers }}</span><span class="ovs-tot">/{{ stats().drivers }}</span></div>
          <div class="ovs-label">Active Drivers <span class="ms-1 small text-warning">{{ stats().onLeave > 0 ? '· ' + stats().onLeave + ' on leave' : '' }}</span></div>
        </div>
      </div>
      <div class="ov-stat gap-3">
        <div class="ovs-icon" style="background:linear-gradient(135deg,#7c3aed,#a78bfa)"><i class="bi bi-people-fill"></i></div>
        <div>
          <div class="ovs-main"><span class="ovs-val">{{ stats().students }}</span><span class="ovs-tot">&nbsp;</span></div>
          <div class="ovs-label">Students Enrolled</div>
        </div>
      </div>
    </div>

    <div class="row g-4">
      <!-- Route Summary -->
      <div class="col-lg-4">
        <div class="tr-card">
          <div class="trc-header"><i class="bi bi-map-fill me-2"></i>Route Summary
            <button class="view-all-btn ms-auto" (click)="activeTab.set('routes')">View All <i class="bi bi-arrow-right ms-1"></i></button>
          </div>
          <div class="trc-body p-0">
            @for (r of routes(); track r.id) {
              <div class="ov-route-row" (click)="activeTab.set('routes'); viewRouteDetail(r)">
                <div class="ovr-icon {{ r.status === 'Active' ? 'ovr-active' : 'ovr-inactive' }}">{{ r.route_code.split('-')[1] }}</div>
                <div class="ovr-info">
                  <div class="ovr-name">{{ r.route_name }}</div>
                  <div class="ovr-meta">{{ r.total_stops }} stops · {{ r.total_distance }} km · {{ getDriverName(r.driver_id) }}</div>
                </div>
                <div class="ovr-right">
                  <div class="ovr-students">{{ r.student_count }} students</div>
                  <span class="{{ getRouteStatusClass(r.status) }}">{{ r.status }}</span>
                </div>
              </div>
            }
          </div>
        </div>
      </div>

      <div class="col-lg-4">
        <!-- Quick Vehicle Status -->
        <div class="tr-card">
          <div class="trc-header"><i class="bi bi-bus-front-fill me-2"></i>Fleet Status
            <button class="view-all-btn ms-auto" (click)="activeTab.set('vehicles')">View All <i class="bi bi-arrow-right ms-1"></i></button>
          </div>
          <div class="trc-body p-0">
            @for (v of vehicles(); track v.id) {
              <div class="ov-vehicle-row">
                <div class="ovv-plate">{{ v.vehicle_no }}</div>
                <div>
                  <div class="small fw-semibold">{{ v.vehicle_name }}</div>
                  <div class="small text-muted">{{ v.make }} {{ v.model }} · {{ v.capacity }} seats</div>
                </div>
                <span class="ms-auto {{ getVehicleStatusClass(v.status) }}">{{ v.status }}</span>
              </div>
            }
          </div>
        </div>
      </div>

      <!-- Expiry Alerts + Vehicle Health -->
      <div class="col-lg-4">
        @if (expiryAlerts().length > 0) {
          <div class="tr-card mb-3">
            <div class="trc-header text-warning"><i class="bi bi-exclamation-triangle-fill me-2"></i>Document Expiry Alerts</div>
            <div class="trc-body p-0">
              @for (alert of expiryAlerts().slice(0,6); track alert.label) {
                @let days = daysUntil(alert.expiry);
                <div class="expiry-row" [class.expired-row]="days <= 0">
                  <i class="bi {{ alert.icon }} me-2" [class.text-danger]="days<=0" [class.text-warning]="days>0"></i>
                  <div class="flex-fill">
                    <div class="small fw-semibold">{{ alert.label }}</div>
                    <div class="small text-muted">{{ formatDate(alert.expiry) }}</div>
                  </div>
                  <span class="days-chip" [class.chip-expired]="days<=0" [class.chip-warning]="days>0&&days<=30">
                    {{ days <= 0 ? 'EXPIRED' : days + ' days' }}
                  </span>
                </div>
              }
            </div>
          </div>
        }
      </div>
    </div>

    <!-- Today's boarding -->
    <div class="tr-card mt-4">
      <div class="trc-header"><i class="bi bi-calendar-check-fill me-2"></i>Today's Transport Usage
        <span class="ms-2 small fw-normal opacity-75">{{ formatDate(todayAttStats().date) }}</span>
        <button class="view-all-btn ms-auto" (click)="setAttTab('attendance')">Mark / Records <i class="bi bi-arrow-right ms-1"></i></button>
      </div>
      <div class="trc-body p-0">
        @if (todayAttStats().marked === 0) {
          <div class="empty-state py-4"><i class="bi bi-clipboard-x"></i><p>No boarding marked for today yet</p></div>
        } @else {
          <div class="today-att-stats">
            <div class="tas-chip tas-boarded"><i class="bi bi-check-circle-fill"></i>{{ todayAttStats().boarded }} boarded</div>
            <div class="tas-chip tas-missed"><i class="bi bi-x-circle-fill"></i>{{ todayAttStats().notBoarded }} not boarded</div>
            <div class="tas-chip tas-leave"><i class="bi bi-calendar-event-fill"></i>{{ todayAttStats().onLeave }} on leave</div>
            <div class="tas-chip tas-am"><i class="bi bi-sunrise-fill"></i>{{ todayAttStats().morningBoarded }} morning</div>
            <div class="tas-chip tas-pm"><i class="bi bi-sunset-fill"></i>{{ todayAttStats().afternoonBoarded }} afternoon</div>
          </div>
          @if (todayByRoute().some(x => x.marked > 0)) {
            <div class="today-route-row">
              @for (item of todayByRoute(); track item.route.id) {
                @if (item.marked > 0) {
                  <div class="trr-item">
                    <span class="trr-code">{{ item.route.route_code }}</span>
                    <span class="trr-name">{{ item.route.route_name }}</span>
                    <span class="trr-count">{{ item.boarded }}/{{ item.total }} boarded</span>
                  </div>
                }
              }
            </div>
          }
          <div class="table-responsive">
            <table class="alloc-table">
              <thead><tr>
                <th>Student</th><th>Class</th><th>Route</th><th>Stop</th>
                <th class="text-center">Morning</th><th class="text-center">Afternoon</th><th>Remark</th>
              </tr></thead>
              <tbody>
                @for (r of todayAttRecords(); track r.id) {
                  <tr>
                    <td>
                      <div class="d-flex align-items-center gap-2">
                        <div class="alloc-avatar">{{ r.student_name.charAt(0) }}</div>
                        <div><div class="fw-semibold small">{{ r.student_name }}</div><div class="small text-muted">{{ r.student_id }}</div></div>
                      </div>
                    </td>
                    <td class="small text-secondary">{{ r.class_name }}</td>
                    <td class="small fw-semibold">{{ getRouteName(r.route_id) }}</td>
                    <td class="small text-muted">{{ r.stop_name }}</td>
                    <td class="text-center"><span class="{{ getBoardingClass(r.morning) }}">{{ r.morning ?? '—' }}</span></td>
                    <td class="text-center"><span class="{{ getBoardingClass(r.afternoon) }}">{{ r.afternoon ?? '—' }}</span></td>
                    <td class="small text-muted">{{ r.remark || '—' }}</td>
                  </tr>
                }
              </tbody>
            </table>
          </div>
        }
      </div>
    </div>
  }

  <!-- ===================== ROUTES ===================== -->
  @if (activeTab() === 'routes') {

    <!-- LIST -->
    @if (routeMode() === 'list') {
      <div class="route-grid">
        @for (r of routes(); track r.id) {
          <div class="route-card" [class.route-inactive]="r.status !== 'Active'">
            <div class="rc-header" [class.rc-inactive]="r.status !== 'Active'">
              <div class="rc-code">{{ r.route_code }}</div>
              <div>
                <h6 class="rc-name">{{ r.route_name }}</h6>
                <div class="rc-desc">{{ r.description }}</div>
              </div>
              <span class="{{ getRouteStatusClass(r.status) }} ms-auto">{{ r.status }}</span>
            </div>
            <div class="rc-stats">
              <div class="rcs-item"><i class="bi bi-signpost-split-fill"></i><span>{{ r.total_stops }} Stops</span></div>
              <div class="rcs-item"><i class="bi bi-speedometer2"></i><span>{{ r.total_distance }} km</span></div>
              <div class="rcs-item"><i class="bi bi-people-fill"></i><span>{{ r.student_count }} Students</span></div>
              <div class="rcs-item"><i class="bi bi-currency-rupee"></i><span>{{ formatCurrency(r.route_fee) }}/mo</span></div>
            </div>
            <div class="rc-assignments">
              <div class="rca-item"><i class="bi bi-bus-front text-primary"></i><span>{{ getVehicleNo(r.vehicle_id) }}</span></div>
              <div class="rca-item"><i class="bi bi-person-circle text-success"></i><span>{{ getDriverName(r.driver_id) }}</span></div>
              <div class="rca-item"><i class="bi bi-clock text-warning"></i><span>{{ r.shift }}</span></div>
              <div class="rca-item"><i class="bi bi-sunrise text-orange"></i><span>{{ r.morning_start }} AM</span></div>
            </div>
            <!-- Stop timeline preview -->
            <div class="stops-preview">
              @for (s of r.stops.slice(0,4); track s.stop_no; let last = $last) {
                <div class="sp-item">
                  <div class="sp-dot" [class.sp-school]="s.stop_no === 1"></div>
                  @if (!last) { <div class="sp-line"></div> }
                  <div class="sp-label">{{ s.stop_name }}</div>
                  <div class="sp-time">{{ s.pickup_time }}</div>
                </div>
              }
              @if (r.stops.length > 4) {
                <div class="sp-more">+{{ r.stops.length - 4 }} more stops</div>
              }
            </div>
            <div class="rc-footer">
              <button class="rcf-btn view"   (click)="viewRouteDetail(r)"><i class="bi bi-eye-fill me-1"></i>Details</button>
              <button class="rcf-btn edit"   (click)="editRoute(r)"><i class="bi bi-pencil-fill me-1"></i>Edit</button>
              <button class="rcf-btn toggle" (click)="toggleRouteStatus(r)"><i class="bi" [class.bi-toggle-on]="r.status==='Active'" [class.bi-toggle-off]="r.status!=='Active'"></i></button>
              <button class="rcf-btn delete" (click)="confirmDelete('route',r.id,r.route_name)"><i class="bi bi-trash3-fill"></i></button>
            </div>
          </div>
        }
        <!-- Add Route card -->
        <div class="add-card" (click)="openNewRoute()">
          <i class="bi bi-plus-circle-fill"></i>
          <span>Add New Route</span>
        </div>
      </div>
    }

    <!-- ROUTE FORM -->
    @if (routeMode() === 'form') {
      <div class="tr-card">
        <div class="trc-header"><i class="bi bi-map-fill me-2"></i>{{ editingRouteId() !== null ? 'Edit Route' : 'New Route' }}</div>
        <div class="trc-body">
          <div class="row g-3 mb-4">
            <div class="col-md-3"><label class="form-label fw-semibold">Route Code <span class="req">*</span></label>
              <input class="form-control text-uppercase" [(ngModel)]="routeForm.route_code" placeholder="e.g. RT-05" [class.is-invalid]="routeSubmitted && !routeForm.route_code"></div>
            <div class="col-md-6"><label class="form-label fw-semibold">Route Name <span class="req">*</span></label>
              <input class="form-control" [(ngModel)]="routeForm.route_name" placeholder="Full route name" [class.is-invalid]="routeSubmitted && !routeForm.route_name"></div>
            <div class="col-md-3"><label class="form-label fw-semibold">Shift</label>
              <select class="form-select" [(ngModel)]="routeForm.shift">@for (s of shiftTypes; track s){ <option [value]="s">{{ s }}</option> }</select></div>
            <div class="col-md-3"><label class="form-label fw-semibold">Assigned Vehicle</label>
              <select class="form-select" [(ngModel)]="routeForm.vehicle_id">
                <option [value]="null">-- None --</option>
                @for (v of vehicles(); track v.id){ <option [value]="v.id">{{ v.vehicle_no }} ({{ v.vehicle_name }})</option> }
              </select></div>
            <div class="col-md-3"><label class="form-label fw-semibold">Assigned Driver</label>
              <select class="form-select" [(ngModel)]="routeForm.driver_id">
                <option [value]="null">-- None --</option>
                @for (d of drivers(); track d.id){ <option [value]="d.id">{{ d.driver_name }}</option> }
              </select></div>
            <div class="col-md-2"><label class="form-label fw-semibold">Morning Start</label><input type="time" class="form-control" [(ngModel)]="routeForm.morning_start"></div>
            <div class="col-md-2"><label class="form-label fw-semibold">Afternoon Start</label><input type="time" class="form-control" [(ngModel)]="routeForm.afternoon_start"></div>
            <div class="col-md-2"><label class="form-label fw-semibold">Monthly Fee (₹)</label><input type="number" class="form-control" [(ngModel)]="routeForm.route_fee" min="0"></div>
            <div class="col-md-2"><label class="form-label fw-semibold">Distance (km)</label><input type="number" class="form-control" [(ngModel)]="routeForm.total_distance" min="0"></div>
            <div class="col-md-2"><label class="form-label fw-semibold">Status</label>
              <select class="form-select" [(ngModel)]="routeForm.status"><option value="Active">Active</option><option value="Inactive">Inactive</option><option value="Suspended">Suspended</option></select></div>
            <div class="col-12"><label class="form-label fw-semibold">Description</label>
              <textarea class="form-control" [(ngModel)]="routeForm.description" rows="2" maxlength="300" placeholder="Route description / coverage area…"></textarea></div>
          </div>
          <!-- Stops -->
          <div class="d-flex align-items-center justify-content-between mb-3">
            <h6 class="sub-label mb-0">Route Stops</h6>
            <button type="button" class="btn btn-add-row" (click)="addStop()"><i class="bi bi-plus-circle me-1"></i>Add Stop</button>
          </div>
          <div class="table-responsive mb-4">
            <table class="stops-table">
              <thead><tr><th>#</th><th>Stop Name</th><th>Landmark</th><th>Distance (km)</th><th>Pickup Time</th><th>Drop Time</th><th></th></tr></thead>
              <tbody>
                @for (s of routeForm.stops ?? []; track s; let i = $index) {
                  <tr>
                    <td class="text-muted small">{{ i+1 }}</td>
                    <!-- <td>
                      <select class="form-select form-select-sm" [(ngModel)]="s.stop_name" [name]="'stop'+i">
                        <option value="">-- Select --</option>
                        @for (sl of stopList; track sl){ <option [value]="sl">{{ sl }}</option> }
                      </select>
                    </td> -->
                    <td><input class="form-control form-control-sm" [(ngModel)]="s.stop_name" [name]="'lm'+i" placeholder="Stopage"></td>
                    <td><input class="form-control form-control-sm" [(ngModel)]="s.landmark" [name]="'lm'+i" placeholder="Landmark"></td>
                    <td><input type="number" class="form-control form-control-sm" [(ngModel)]="s.distance" [name]="'dist'+i" min="0"></td>
                    <td><input type="time" class="form-control form-control-sm" [(ngModel)]="s.pickup_time" [name]="'pt'+i"></td>
                    <td><input type="time" class="form-control form-control-sm" [(ngModel)]="s.drop_time" [name]="'dt'+i"></td>
                    <td><button type="button" class="btn-remove-row" (click)="removeStop(i)"><i class="bi bi-x-lg"></i></button></td>
                  </tr>
                }
              </tbody>
            </table>
          </div>
          <div class="d-flex justify-content-end gap-3">
            <button class="btn btn-outline-secondary px-4" (click)="routeMode.set('list')">Cancel</button>
            <button class="btn tr-btn-primary px-5" (click)="saveRoute()"><i class="bi bi-check-circle-fill me-2"></i>{{ editingRouteId() !== null ? 'Update' : 'Save' }}</button>
          </div>
        </div>
      </div>
    }

    <!-- ROUTE DETAIL -->
    @if (routeMode() === 'detail' && getViewRoute()) {
      @let r = getViewRoute()!;
      <div class="row g-4">
        <div class="col-lg-4">
          <div class="tr-card">
            <div class="trc-header"><i class="bi bi-map-fill me-2"></i>{{ r.route_code }}</div>
            <div class="trc-body">
              <h5 class="fw-bold mb-1">{{ r.route_name }}</h5>
              <span class="{{ getRouteStatusClass(r.status) }}">{{ r.status }}</span>
              <p class="small text-muted mt-2 mb-3">{{ r.description }}</p>
              <div class="detail-list">
                <div class="dl-row"><span>Vehicle</span><span class="fw-semibold">{{ getVehicleNo(r.vehicle_id) }}</span></div>
                <div class="dl-row"><span>Driver</span><span class="fw-semibold">{{ getDriverName(r.driver_id) }}</span></div>
                <div class="dl-row"><span>Shift</span><span>{{ r.shift }}</span></div>
                <div class="dl-row"><span>Morning Start</span><span>{{ r.morning_start }}</span></div>
                <div class="dl-row"><span>Afternoon Start</span><span>{{ r.afternoon_start }}</span></div>
                <div class="dl-row"><span>Total Distance</span><span>{{ r.total_distance }} km</span></div>
                <div class="dl-row"><span>Students</span><span class="fw-bold text-primary">{{ r.student_count }}</span></div>
                <div class="dl-row"><span>Monthly Fee</span><span class="fw-bold text-success">{{ formatCurrency(r.route_fee) }}</span></div>
              </div>
              <button class="btn tr-btn-primary btn-sm w-100 mt-3" (click)="editRoute(r)"><i class="bi bi-pencil-fill me-1"></i>Edit Route</button>
            </div>
          </div>
        </div>
        <div class="col-lg-8">
          <div class="tr-card">
            <div class="trc-header"><i class="bi bi-signpost-split-fill me-2"></i>Stop Details ({{ r.stops.length }} stops)</div>
            <div class="trc-body p-0">
              <div class="stops-timeline">
                @for (s of r.stops; track s.stop_no; let last=$last) {
                  <div class="st-item">
                    <div class="st-marker" [class.st-school]="s.stop_no===1" [class.st-last]="last">
                      <div class="st-dot"></div>
                      @if (!last) { <div class="st-line"></div> }
                    </div>
                    <div class="st-content">
                      <div class="st-name">{{ s.stop_name }} @if (s.stop_no===1) { <span class="badge bg-primary-subtle text-primary small ms-1">School</span> }</div>
                      @if (s.landmark) { <div class="st-landmark"><i class="bi bi-geo-alt me-1"></i>{{ s.landmark }}</div> }
                      <div class="st-times">
                        <span><i class="bi bi-sunrise me-1"></i>Pick: {{ s.pickup_time }}</span>
                        <span class="ms-3"><i class="bi bi-sunset me-1"></i>Drop: {{ s.drop_time }}</span>
                        <span class="ms-3"><i class="bi bi-speedometer me-1"></i>{{ s.distance }} km</span>
                      </div>
                    </div>
                  </div>
                }
              </div>
            </div>
          </div>
        </div>
      </div>
    }
  }

  <!-- ===================== VEHICLES ===================== -->
  @if (activeTab() === 'vehicles') {

    @if (vehicleMode() === 'list') {
      <!-- Filters -->
      <div class="tr-card mb-3">
        <div class="trc-body">
          <div class="row g-3 align-items-end">
            <div class="col-md-5"><label class="form-label small fw-semibold text-muted">Search</label>
              <div class="input-group"><span class="input-group-text bg-light border-end-0"><i class="bi bi-search text-muted"></i></span>
                <input class="form-control border-start-0" placeholder="Vehicle no., make, model…" [(ngModel)]="vehicleSearch">
                @if (vehicleSearch) { <button class="btn btn-outline-secondary" (click)="vehicleSearch=''"><i class="bi bi-x"></i></button> }
              </div></div>
            <div class="col-md-3"><label class="form-label small fw-semibold text-muted">Status</label>
              <select class="form-select" [(ngModel)]="vehicleStatusFilter">
                <option value="">All Statuses</option>
                @for (s of ['Active','Inactive','Under Maintenance','Retired']; track s){ <option [value]="s">{{ s }}</option> }
              </select></div>
          </div>
        </div>
      </div>

      <div class="vehicles-grid">
        @for (v of filteredVehicles(); track v.id) {
          <div class="vehicle-card" [class.vc-maintenance]="v.status==='Under Maintenance'">
            <div class="vc-header">
              <div class="vc-plate">{{ v.vehicle_no }}</div>
              <span class="{{ getVehicleStatusClass(v.status) }}">{{ v.status }}</span>
            </div>
            <div class="vc-name">{{ v.vehicle_name }}</div>
            <div class="vc-make">{{ v.make }} {{ v.model }} · {{ v.model_year }}</div>
            <div class="vc-specs">
              <div class="vcs-item"><i class="bi bi-people-fill"></i>{{ v.capacity }} seats</div>
              <div class="vcs-item"><i class="bi bi-fuel-pump-fill"></i>{{ v.fuel_type }}</div>
              <div class="vcs-item"><i class="bi bi-tag-fill"></i>{{ v.vehicle_type }}</div>
            </div>
            <!-- Doc status chips -->
            <div class="doc-chips">
              <span class="dc-chip {{ getDocStatus(v.insurance_expiry) === 'valid' ? 'dc-ok' : getDocStatus(v.insurance_expiry) === 'expiring' ? 'dc-warn' : 'dc-expired' }}" [title]="'Insurance: ' + formatDate(v.insurance_expiry)">
                <i class="bi bi-shield-fill"></i> Ins {{ isExpired(v.insurance_expiry) ? 'Expired' : isExpiringSoon(v.insurance_expiry) ? '!' : '✓' }}
              </span>
              <span class="dc-chip {{ getDocStatus(v.fitness_expiry) === 'valid' ? 'dc-ok' : getDocStatus(v.fitness_expiry) === 'expiring' ? 'dc-warn' : 'dc-expired' }}" [title]="'Fitness: ' + formatDate(v.fitness_expiry)">
                <i class="bi bi-clipboard2-check-fill"></i> Fit {{ isExpired(v.fitness_expiry) ? 'Expired' : isExpiringSoon(v.fitness_expiry) ? '!' : '✓' }}
              </span>
              <span class="dc-chip {{ getDocStatus(v.next_service) === 'valid' ? 'dc-ok' : 'dc-warn' }}" [title]="'Next service: ' + formatDate(v.next_service)">
                <i class="bi bi-tools"></i> Svc {{ isExpiringSoon(v.next_service) ? 'Due' : '✓' }}
              </span>
            </div>
            @if (v.odometer) { <div class="vc-odo"><i class="bi bi-speedometer2 me-1"></i>{{ v.odometer.toLocaleString('en-IN') }} km</div> }
            @if (v.remarks) { <div class="vc-remarks">{{ v.remarks }}</div> }
            <div class="vc-footer">
              <button class="vcf-btn edit"   (click)="editVehicle(v)"><i class="bi bi-pencil-fill me-1"></i>Edit</button>
              <button class="vcf-btn toggle" (click)="toggleVehicleStatus(v)"><i class="bi" [class.bi-toggle-on]="v.status==='Active'" [class.bi-toggle-off]="v.status!=='Active'"></i></button>
              <button class="vcf-btn delete" (click)="confirmDelete('vehicle',v.id,v.vehicle_no)"><i class="bi bi-trash3-fill"></i></button>
            </div>
          </div>
        }
        <div class="add-card" (click)="openNewVehicle()"><i class="bi bi-plus-circle-fill"></i><span>Add Vehicle</span></div>
      </div>
    }

    @if (vehicleMode() === 'form') {
      <div class="tr-card">
        <div class="trc-header"><i class="bi bi-bus-front-fill me-2"></i>{{ editingVehicleId() !== null ? 'Edit Vehicle' : 'Add Vehicle' }}</div>
        <div class="trc-body">
          <div class="row g-3">
            <div class="col-md-4"><label class="form-label fw-semibold">Vehicle Number <span class="req">*</span></label>
              <input class="form-control text-uppercase" [(ngModel)]="vehicleForm.vehicle_no" placeholder="DL 01 AB 1234" [class.is-invalid]="vehicleSubmitted && !vehicleForm.vehicle_no"></div>
            <div class="col-md-5"><label class="form-label fw-semibold">Vehicle Name / Label</label>
              <input class="form-control" [(ngModel)]="vehicleForm.vehicle_name" placeholder="e.g. Bus 5 – North Extension"></div>
            <div class="col-md-3"><label class="form-label fw-semibold">Status</label>
              <select class="form-select" [(ngModel)]="vehicleForm.status">@for(s of ['Active','Inactive','Under Maintenance','Retired'];track s){<option [value]="s">{{ s }}</option>}</select></div>
            <div class="col-md-4"><label class="form-label fw-semibold">Vehicle Type</label>
              <select class="form-select" [(ngModel)]="vehicleForm.vehicle_type">@for(t of vehicleTypes;track t){<option [value]="t">{{ t }}</option>}</select></div>
            <div class="col-md-3"><label class="form-label fw-semibold">Make</label><input class="form-control" [(ngModel)]="vehicleForm.make" placeholder="e.g. Tata"></div>
            <div class="col-md-3"><label class="form-label fw-semibold">Model</label><input class="form-control" [(ngModel)]="vehicleForm.model" placeholder="e.g. StarBus"></div>
            <div class="col-md-2"><label class="form-label fw-semibold">Year</label><input type="number" class="form-control" [(ngModel)]="vehicleForm.model_year" min="1990" max="2030"></div>
            <div class="col-md-2"><label class="form-label fw-semibold">Capacity</label><input type="number" class="form-control" [(ngModel)]="vehicleForm.capacity" min="1"></div>
            <div class="col-md-3"><label class="form-label fw-semibold">Fuel Type</label>
              <select class="form-select" [(ngModel)]="vehicleForm.fuel_type">@for(f of fuelTypes;track f){<option [value]="f">{{ f }}</option>}</select></div>
            <div class="col-md-5"><label class="form-label fw-semibold">GPS Device ID</label><input class="form-control" [(ngModel)]="vehicleForm.gps_device_id" placeholder="GPS tracker ID"></div>
            <div class="col-md-4"><label class="form-label fw-semibold">Engine No.</label><input class="form-control" [(ngModel)]="vehicleForm.engine_no"></div>
            <div class="col-md-4"><label class="form-label fw-semibold">Chassis No.</label><input class="form-control" [(ngModel)]="vehicleForm.chassis_no"></div>
            <div class="col-md-4"><label class="form-label fw-semibold">Odometer (km)</label><input type="number" class="form-control" [(ngModel)]="vehicleForm.odometer" min="0"></div>

            <div class="col-12"><div class="form-section-divider">Document Validity Dates</div></div>
            <div class="col-md-3"><label class="form-label fw-semibold">Insurance Expiry</label><input type="date" class="form-control" [(ngModel)]="vehicleForm.insurance_expiry"></div>
            <div class="col-md-3"><label class="form-label fw-semibold">Fitness Cert Expiry</label><input type="date" class="form-control" [(ngModel)]="vehicleForm.fitness_expiry"></div>
            <div class="col-md-3"><label class="form-label fw-semibold">Permit Expiry</label><input type="date" class="form-control" [(ngModel)]="vehicleForm.permit_expiry"></div>
            <div class="col-md-3"><label class="form-label fw-semibold">Last Service</label><input type="date" class="form-control" [(ngModel)]="vehicleForm.last_service"></div>
            <div class="col-md-3"><label class="form-label fw-semibold">Next Service Due</label><input type="date" class="form-control" [(ngModel)]="vehicleForm.next_service"></div>
            <div class="col-12"><label class="form-label fw-semibold">Remarks</label>
              <textarea class="form-control" [(ngModel)]="vehicleForm.remarks" rows="2" maxlength="300" placeholder="Any notes about this vehicle…"></textarea></div>
          </div>
          <div class="d-flex justify-content-end gap-3 mt-4">
            <button class="btn btn-outline-secondary px-4" (click)="vehicleMode.set('list')">Cancel</button>
            <button class="btn tr-btn-primary px-5" (click)="saveVehicle()"><i class="bi bi-check-circle-fill me-2"></i>{{ editingVehicleId() !== null ? 'Update' : 'Save' }}</button>
          </div>
        </div>
      </div>
    }
  }

  <!-- ===================== DRIVERS ===================== -->
  @if (activeTab() === 'drivers') {

    @if (driverMode() === 'list') {
      <div class="tr-card mb-3"><div class="trc-body">
        <div class="input-group" style="max-width:400px">
          <span class="input-group-text bg-light border-end-0"><i class="bi bi-search text-muted"></i></span>
          <input class="form-control border-start-0" placeholder="Name, code, mobile, license…" [(ngModel)]="driverSearch">
        </div>
      </div></div>
      <div class="drivers-grid">
        @for (d of filteredDrivers(); track d.id) {
          <div class="driver-card">
            <div class="dc-top">
              <div class="dc-avatar">{{ getInitials(d.driver_name) }}</div>
              <div class="dc-info">
                <div class="dc-name">{{ d.driver_name }}</div>
                <div class="dc-code">{{ d.emp_code }}</div>
                <span class="{{ getDriverStatusClass(d.status) }}">{{ d.status }}</span>
              </div>
            </div>
            <div class="dc-details">
              <div class="dcd-row"><i class="bi bi-telephone-fill text-success"></i><span>{{ d.mobile }}</span></div>
              <div class="dcd-row"><i class="bi bi-card-text text-primary"></i><span>{{ d.license_no }} ({{ d.license_type }})</span></div>
              <div class="dcd-row" [class.text-danger]="isExpired(d.license_expiry)" [class.text-warning]="!isExpired(d.license_expiry) && isExpiringSoon(d.license_expiry)">
                <i class="bi bi-calendar-x-fill"></i>
                <span>License exp: {{ formatDate(d.license_expiry) }} {{ isExpired(d.license_expiry) ? '⚠ EXPIRED' : isExpiringSoon(d.license_expiry) ? '⚠ Soon' : '' }}</span>
              </div>
              <div class="dcd-row"><i class="bi bi-map-fill text-warning"></i><span>{{ getRouteName(d.assigned_routeid) }}</span></div>
              <div class="dcd-row"><i class="bi bi-bus-front-fill text-info"></i><span>{{ getVehicleNo(d.assigned_vehicleid) }}</span></div>
              <div class="dcd-row"><i class="bi bi-briefcase-fill text-muted"></i><span>{{ d.experience_years }} yrs experience</span></div>
            </div>
            @if (d.remarks) { <div class="dc-remark">{{ d.remarks }}</div> }
            <div class="dc-footer">
              <button class="dcf-btn edit"   (click)="editDriver(d)"><i class="bi bi-pencil-fill me-1"></i>Edit</button>
              <button class="dcf-btn toggle" (click)="toggleDriverStatus(d)"><i class="bi" [class.bi-toggle-on]="d.status==='Active'" [class.bi-toggle-off]="d.status!=='Active'"></i></button>
              <button class="dcf-btn delete" (click)="confirmDelete('driver',d.id,d.driver_name)"><i class="bi bi-trash3-fill"></i></button>
            </div>
          </div>
        }
        <div class="add-card" (click)="openNewDriver()"><i class="bi bi-plus-circle-fill"></i><span>Add Driver</span></div>
      </div>
    }

    @if (driverMode() === 'form') {
      <div class="tr-card">
        <div class="trc-header"><i class="bi bi-person-badge-fill me-2"></i>{{ editingDriverId() !== null ? 'Edit Driver' : 'Add Driver' }}</div>
        <div class="trc-body">
          <div class="row g-3">
            <div class="col-md-3"><label class="form-label fw-semibold">Employee Code</label>
              <input class="form-control" [(ngModel)]="driverForm.emp_code" placeholder="EMP-2026-001"
              (keyup.enter)="onSearchStaff()" (keydown.Tab)="onSearchStaff()"></div>
            <div class="col-md-5"><label class="form-label fw-semibold">Full Name <span class="req">*</span></label>
              <input class="form-control" [(ngModel)]="driverForm.driver_name" placeholder="Driver full name" [class.is-invalid]="driverSubmitted && !driverForm.driver_name"></div>
            <div class="col-md-2"><label class="form-label fw-semibold">Date of Birth</label><input type="date" class="form-control" [(ngModel)]="driverForm.driver_dob"></div>
            <div class="col-md-2"><label class="form-label fw-semibold">Joining Date</label><input type="date" class="form-control" [(ngModel)]="driverForm.joining_date"></div>
            <div class="col-md-4"><label class="form-label fw-semibold">Mobile <span class="req">*</span></label>
              <div class="input-group"><span class="input-group-text bg-light">+91</span>
                <input class="form-control" [(ngModel)]="driverForm.mobile" maxlength="10" placeholder="10-digit" [class.is-invalid]="driverSubmitted && !driverForm.mobile"></div></div>
            <div class="col-md-4"><label class="form-label fw-semibold">Alt Mobile</label>
              <div class="input-group"><span class="input-group-text bg-light">+91</span><input class="form-control" [(ngModel)]="driverForm.alt_mobile" maxlength="10"></div></div>
            <div class="col-md-4"><label class="form-label fw-semibold">Email</label><input type="email" class="form-control" [(ngModel)]="driverForm.email" placeholder="driver@email.com"></div>
            <div class="col-md-4"><label class="form-label fw-semibold">License No.</label><input class="form-control" [(ngModel)]="driverForm.license_no" placeholder="DL-01..."></div>
            <div class="col-md-3"><label class="form-label fw-semibold">License Type</label>
              <select class="form-select" [(ngModel)]="driverForm.license_type">@for(t of licenseTypes;track t){<option [value]="t">{{ t }}</option>}</select></div>
            <div class="col-md-3"><label class="form-label fw-semibold">License Expiry</label><input type="date" class="form-control" [(ngModel)]="driverForm.license_expiry"></div>
            <div class="col-md-2"><label class="form-label fw-semibold">Experience (yrs)</label><input type="number" class="form-control" [(ngModel)]="driverForm.experience_years" min="0"></div>
            <div class="col-md-4"><label class="form-label fw-semibold">Aadhaar No.</label><input class="form-control" [(ngModel)]="driverForm.aadhar_no" maxlength="12" placeholder="12-digit Aadhaar"></div>
            <div class="col-md-3"><label class="form-label fw-semibold">Assign Route</label>
              <select class="form-select" [(ngModel)]="driverForm.assigned_routeid">
                <option [value]="null">-- None --</option>
                @for (r of routes();track r.id){<option [value]="r.id">{{ r.route_code }} – {{ r.route_name }}</option>}
              </select></div>
            <div class="col-md-3"><label class="form-label fw-semibold">Assign Vehicle</label>
              <select class="form-select" [(ngModel)]="driverForm.assigned_vehicleid">
                <option [value]="null">-- None --</option>
                @for (v of vehicles();track v.id){<option [value]="v.id">{{ v.vehicle_no }}</option>}
              </select></div>
            <div class="col-md-2"><label class="form-label fw-semibold">Status</label>
              <select class="form-select" [(ngModel)]="driverForm.status">@for(s of ['Active','On Leave','Inactive'];track s){<option [value]="s">{{ s }}</option>}</select></div>
            <div class="col-12"><label class="form-label fw-semibold">Address</label><textarea class="form-control" [(ngModel)]="driverForm.address" rows="2" maxlength="300"></textarea></div>
            <div class="col-12"><label class="form-label fw-semibold">Remarks</label><textarea class="form-control" [(ngModel)]="driverForm.remarks" rows="2" maxlength="300"></textarea></div>
          </div>
          <div class="d-flex justify-content-end gap-3 mt-4">
            <button class="btn btn-outline-secondary px-4" (click)="driverMode.set('list')">Cancel</button>
            <button class="btn tr-btn-primary px-5" (click)="saveDriver()"><i class="bi bi-check-circle-fill me-2"></i>{{ editingDriverId() !== null ? 'Update' : 'Save' }}</button>
          </div>
        </div>
      </div>
    }
  }

  <!-- ===================== ALLOCATIONS ===================== -->
  @if (activeTab() === 'allocations') {

    @if (allocMode() === 'list') {
      <!-- Filters -->
      <div class="tr-card mb-3"><div class="trc-body">
        <div class="row g-3 align-items-end">
          <div class="col-md-4"><label class="form-label small fw-semibold text-muted">Search</label>
            <div class="input-group"><span class="input-group-text bg-light border-end-0"><i class="bi bi-search text-muted"></i></span>
              <input class="form-control border-start-0" placeholder="Name, ID, class…" [(ngModel)]="allocSearch">
              @if (allocSearch) {<button class="btn btn-outline-secondary" (click)="allocSearch=''"><i class="bi bi-x"></i></button>}
            </div></div>
          <div class="col-md-3"><label class="form-label small fw-semibold text-muted">Route</label>
            <select class="form-select" [(ngModel)]="allocRoute">
              <option [value]="0">All Routes</option>
              @for (r of routes();track r.id){
                <option [value]="r.id">{{ r.route_code }} - {{ r.route_name }}</option>
              }
            </select></div>
          <div class="col-md-2"><label class="form-label small fw-semibold text-muted">Status</label>
            <select class="form-select" [(ngModel)]="allocStatus">
              <option value="">All</option>@for(s of ['Active','Inactive','On Leave'];track s){<option [value]="s">{{ s }}</option>}
            </select></div>
          <div class="col-auto"><button class="btn btn-outline-secondary" (click)="allocSearch='';allocRoute=0;allocStatus=''"><i class="bi bi-arrow-counterclockwise me-1"></i>Reset</button></div>
        </div>
        <small class="text-muted mt-2 d-block">{{ filteredAllocs().length }} allocations</small>
      </div></div>

      <div class="tr-card"><div class="trc-body p-0">
        @if (filteredAllocs().length === 0) {
          <div class="empty-state"><i class="bi bi-people"></i><p>No allocations found</p></div>
        } @else {
          <div class="table-responsive">
            <table class="alloc-table">
              <thead><tr>
                <th>Student</th><th>Class</th><th>Route</th><th>Stop</th>
                <th>Shift</th><th class="text-end">Monthly Fee</th>
                <th>Start</th><th>Status</th><th class="text-center">Actions</th>
              </tr></thead>
              <tbody>
                @for (a of filteredAllocs(); track a.id) {
                  <tr>
                    <td>
                      <div class="d-flex align-items-center gap-2">
                        <div class="alloc-avatar">{{ a.student_name.charAt(0) }}</div>
                        <div><div class="fw-semibold small">{{ a.student_name }}</div><div class="small text-muted">{{ a.student_id }}</div></div>
                      </div>
                    </td>
                    <td class="small text-secondary">{{ a.class_name }}</td>
                    <td class="small fw-semibold">{{ getRouteName(a.route_id) }}</td>
                    <td class="small text-muted">{{ a.stop_name }}</td>
                    <td><span class="shift-badge">{{ a.shift }}</span></td>
                    <td class="text-end fw-semibold small text-primary">{{ formatCurrency(a.route_fee) }}</td>
                    <td class="small text-muted">{{ formatDate(a.start_date) }}</td>
                    <td><span class="{{ getAllocStatusClass(a.status) }}">{{ a.status }}</span></td>
                    <td class="text-center">
                      <div class="alloc-actions">
                        <button class="aa-btn" (click)="editAlloc(a)" title="Edit"><i class="bi bi-pencil-fill"></i></button>
                        <button class="aa-btn" (click)="toggleAllocStatus(a)" title="Toggle"><i class="bi" [class.bi-toggle-on]="a.status==='Active'" [class.bi-toggle-off]="a.status!=='Active'"></i></button>
                        <button class="aa-btn danger" (click)="confirmDelete('alloc',a.id,a.student_name)" title="Delete"><i class="bi bi-trash3-fill"></i></button>
                      </div>
                    </td>
                  </tr>
                }
              </tbody>
            </table>
          </div>
        }
      </div></div>
    }

    @if (allocMode() === 'form') {
      <div class="tr-card" style="max-width:700px">
        <div class="trc-header"><i class="bi bi-people-fill me-2"></i>{{ editingAllocId() !== null ? 'Edit Allocation' : 'New Student Allocation' }}</div>
        <div class="trc-body">
          <div class="row g-3">
            <div class="col-md-5"><label class="form-label fw-semibold">Student ID <span class="req">*</span></label>
              <input class="form-control" [(ngModel)]="allocForm.student_id" placeholder="e.g. 515580" 
              [class.is-invalid]="allocSubmitted && !allocForm.student_id"
              (keyup.enter)="onSearchStd()" (keydown.Tab)="onSearchStd()"></div>
            <div class="col-md-7"><label class="form-label fw-semibold">Student Name <span class="req">*</span></label>
              <input class="form-control" [(ngModel)]="allocForm.student_name" placeholder="Full name"></div>
            <div class="col-md-5"><label class="form-label fw-semibold">Class</label>
              <select class="form-select" [(ngModel)]="allocForm.class_name">
                <option value="">--</option>@for(c of classOptions;track c){<option [value]="c.code">{{ c.name }}</option>}
              </select></div>
            <div class="col-md-4"><label class="form-label fw-semibold">Roll No.</label><input class="form-control" [(ngModel)]="allocForm.roll_no" placeholder="e.g. 11-A-01"></div>
            <div class="col-md-7"><label class="form-label fw-semibold">Route <span class="req">*</span></label>
              <select class="form-select" [(ngModel)]="allocForm.route_id" (change)="onAllocRouteChange()" [class.is-invalid]="allocSubmitted && !allocForm.route_id">
                <option [value]="0">-- Select Route --</option>
                @for (r of routes().filter(r => r.status==='Active');track r.id){<option [value]="r.id">{{ r.route_code }} – {{ r.route_name }}</option>}
              </select></div>
            <div class="col-md-5"><label class="form-label fw-semibold">Boarding Stop</label>
              <select class="form-select" [(ngModel)]="allocForm.stop_name">
                <option value="">-- Select --</option>
                @let selRoute = routes().find(r => r.id === +allocForm.route_id!);
                @for (s of selRoute?.stops ?? [];track s.stop_no){<option [value]="s.stop_name">{{ s.stop_name }}</option>}
              </select></div>
            <div class="col-md-3"><label class="form-label fw-semibold">Shift</label>
              <select class="form-select" [(ngModel)]="allocForm.shift">@for(s of shiftTypes;track s){<option [value]="s">{{ s }}</option>}</select></div>
            <div class="col-md-3"><label class="form-label fw-semibold">Monthly Fee (₹)</label><input type="number" class="form-control" [(ngModel)]="allocForm.route_fee" min="0"></div>
            <div class="col-md-3"><label class="form-label fw-semibold">Status</label>
              <select class="form-select" [(ngModel)]="allocForm.status">@for(s of ['Active','Inactive','On Leave'];track s){<option [value]="s">{{ s }}</option>}</select></div>
            <div class="col-md-3"><label class="form-label fw-semibold">Start Date</label><input type="date" class="form-control" [(ngModel)]="allocForm.start_date"></div>
            <div class="col-md-3"><label class="form-label fw-semibold">End Date</label><input type="date" class="form-control" [(ngModel)]="allocForm.end_date"></div>
            <div class="col-12"><label class="form-label fw-semibold">Remarks</label><textarea class="form-control" [(ngModel)]="allocForm.remarks" rows="2" maxlength="200"></textarea></div>
          </div>
          <div class="d-flex justify-content-end gap-3 mt-4">
            <button class="btn btn-outline-secondary px-4" (click)="allocMode.set('list')">Cancel</button>
            <button class="btn tr-btn-primary px-5" (click)="saveAlloc()"><i class="bi bi-check-circle-fill me-2"></i>{{ editingAllocId() !== null ? 'Update' : 'Save' }}</button>
          </div>
        </div>
      </div>
    }
  }

  <!-- ===================== DAILY ATTENDANCE ===================== -->
  @if (activeTab() === 'attendance') {

    <div class="att-subtabs mb-3">
      @for (t of [{k:'mark',l:'Mark Attendance',ic:'bi-pencil-fill'},{k:'records',l:'Daily Records',ic:'bi-table'}]; track t.k) {
        <button class="att-subtab" [class.active]="attSubTab() === t.k" (click)="attSubTab.set($any(t.k))">
          <i class="bi {{ t.ic }} me-1"></i>{{ t.l }}
        </button>
      }
    </div>

    <!-- MARK -->
    @if (attSubTab() === 'mark') {
      <div class="tr-card mb-3">
        <div class="trc-body">
          <div class="row g-3 align-items-end">
            <div class="col-md-2">
              <label class="form-label small fw-semibold text-muted">Date</label>
              <input type="date" class="form-control" [ngModel]="attDate()" (ngModelChange)="onAttDateChange($event)">
            </div>
            <div class="col-md-3">
              <label class="form-label small fw-semibold text-muted">Route</label>
              <select class="form-select" [ngModel]="attRoute()" (ngModelChange)="onAttRouteChange($event)">
                <option [ngValue]="0">All Active Routes</option>
                @for (r of routes(); track r.id) {
                  <option [ngValue]="r.id">{{ r.route_code }} – {{ r.route_name }}</option>
                }
              </select>
            </div>
            <div class="col-md-5">
              <label class="form-label small fw-semibold text-muted">Search Student</label>
              <div class="input-group">
                <span class="input-group-text bg-light border-end-0"><i class="bi bi-search text-muted"></i></span>
                <input class="form-control border-start-0" placeholder="Name, ID, stop…"
                  [ngModel]="attSearch()" (ngModelChange)="attSearch.set($event)">
                @if (attSearch()) {
                  <button class="btn btn-outline-secondary" (click)="attSearch.set('')"><i class="bi bi-x"></i></button>
                }
              </div>
            </div>
            <div class="col-auto">
              <button class="btn tr-btn-primary" (click)="saveAttendance()" [disabled]="!attRows().length || attSaved()">
                @if (attSaved()) { <i class="bi bi-check-circle-fill me-1"></i>Saved }
                @else { <i class="bi bi-save-fill me-1"></i>Save Att }
              </button>
            </div>
          </div>
          <div class="d-flex flex-wrap gap-2 mt-3 align-items-center">
            <span class="small fw-semibold text-muted me-1">Bulk mark:</span>
            <button class="bulk-att boarded" (click)="setAllBoarding('Both','Boarded')">All Boarded</button>
            <button class="bulk-att missed" (click)="setAllBoarding('Both','Not Boarded')">All Not Boarded</button>
            <button class="bulk-att leave" (click)="setAllBoarding('Both','Leave')">All Leave</button>
            <button class="bulk-att boarded" (click)="setAllBoarding('Morning','Boarded')">Morning Boarded</button>
            <button class="bulk-att boarded" (click)="setAllBoarding('Afternoon','Boarded')">Afternoon Boarded</button>
          </div>
        </div>
      </div>

      <!-- <div class="att-mark-stats mb-3">
        <div class="ams-item"><span class="ams-val">{{ attMarkSummary().total }}</span><span class="ams-lbl">Allocated</span></div>
        <div class="ams-item ams-ok"><span class="ams-val">{{ attMarkSummary().boarded }}</span><span class="ams-lbl">Used transport</span></div>
        <div class="ams-item ams-bad"><span class="ams-val">{{ attMarkSummary().notBoarded }}</span><span class="ams-lbl">Not boarded</span></div>
        <div class="ams-item ams-leave"><span class="ams-val">{{ attMarkSummary().onLeave }}</span><span class="ams-lbl">On leave</span></div>
        <div class="ams-item"><span class="ams-val">{{ attMarkSummary().morningBoarded }}</span><span class="ams-lbl">Morning boarded</span></div>
        <div class="ams-item"><span class="ams-val">{{ attMarkSummary().afternoonBoarded }}</span><span class="ams-lbl">Afternoon boarded</span></div>
      </div> -->

      <div class="tr-card">
        <div class="trc-header">
          <i class="bi bi-clipboard-check me-2"></i>Who used transport on {{ formatDate(attDate()) }}
          <span class="ms-auto small fw-normal">{{ filteredAttRows().length }} student(s)</span>
        </div>
        <div class="trc-body p-0">
          @if (filteredAttRows().length === 0) {
            <div class="empty-state">
              <i class="bi bi-people"></i>
              <p>{{ attRows().length === 0 ? 'No active allocations for this route' : 'No students match your search' }}</p>
            </div>
          } @else {
            <div class="table-responsive">
              <table class="alloc-table att-mark-table">
                <thead>
                  <tr>
                    <th>Student</th>
                    <th>Class</th>
                    <th>Route</th>
                    <th>Stop</th>
                    <th>Shift</th>
                    <th class="text-center">Morning</th>
                    <th class="text-center">Afternoon</th>
                    <th>Remark</th>
                  </tr>
                </thead>
                <tbody>
                  @for (row of filteredAttRows(); track row.student_id) {
                    <tr [class.row-missed]="!usedTransport(row) && row.morning !== 'Leave' && row.afternoon !== 'Leave'"
                        [class.row-leave]="row.morning === 'Leave' || row.afternoon === 'Leave'">
                      <td>
                        <div class="d-flex align-items-center gap-2">
                          <div class="alloc-avatar" [class.av-missed]="!usedTransport(row)">{{ row.student_name.charAt(0) }}</div>
                          <div>
                            <div class="fw-semibold small">{{ row.student_name }}</div>
                            <div class="small text-muted">{{ row.student_id }} · {{ row.roll_no }}</div>
                          </div>
                        </div>
                      </td>
                      <td class="small text-secondary">{{ row.class_name }}</td>
                      <td class="small fw-semibold">{{ getRouteName(row.route_id) }}</td>
                      <td class="small text-muted">{{ row.stop_name }}</td>
                      <td><span class="shift-badge">{{ row.shift }}</span></td>
                      <td>
                        @if (row.morning === null) {
                          <span class="bd-na">N/A</span>
                        } @else {
                          <div class="board-btns">
                            @for (st of boardingStatuses; track st) {
                              <button type="button" class="bbtn"
                                [class.active]="row.morning === st"
                                [class.bb-ok]="st==='Boarded'"
                                [class.bb-no]="st==='Not Boarded'"
                                [class.bb-lv]="st==='Leave'"
                                (click)="setBoarding(row.student_id,'Morning',st)">
                                {{ st === 'Not Boarded' ? 'No' : st === 'Boarded' ? 'Yes' : 'Leave' }}
                              </button>
                            }
                          </div>
                        }
                      </td>
                      <td>
                        @if (row.afternoon === null) {
                          <span class="bd-na">N/A</span>
                        } @else {
                          <div class="board-btns">
                            @for (st of boardingStatuses; track st) {
                              <button type="button" class="bbtn"
                                [class.active]="row.afternoon === st"
                                [class.bb-ok]="st==='Boarded'"
                                [class.bb-no]="st==='Not Boarded'"
                                [class.bb-lv]="st==='Leave'"
                                (click)="setBoarding(row.student_id,'Afternoon',st)">
                                {{ st === 'Not Boarded' ? 'No' : st === 'Boarded' ? 'Yes' : 'Leave' }}
                              </button>
                            }
                          </div>
                        }
                      </td>
                      <td>
                        <input class="form-control form-control-sm" [(ngModel)]="row.remark" [name]="'rem_'+row.student_id"
                          placeholder="Optional note" (ngModelChange)="attSaved.set(false)">
                      </td>
                    </tr>
                  }
                </tbody>
              </table>
            </div>
          }
        </div>
      </div>
    }

    <!-- RECORDS -->
    @if (attSubTab() === 'records') {
      <div class="tr-card mb-3">
        <div class="trc-body">
          <div class="row g-3 align-items-end">
            <div class="col-md-2">
              <label class="form-label small fw-semibold text-muted">Date</label>
              <input type="date" class="form-control" [ngModel]="attRecDate()" (ngModelChange)="attRecDate.set($event)">
            </div>
            <div class="col-md-3">
              <label class="form-label small fw-semibold text-muted">Route</label>
              <select class="form-select" [ngModel]="attRecRoute()" (ngModelChange)="attRecRoute.set(+$event)">
                <option [ngValue]="0">All Routes</option>
                @for (r of routes(); track r.id) {
                  <option [ngValue]="r.id">{{ r.route_code }} – {{ r.route_name }}</option>
                }
              </select>
            </div>
            <div class="col-md-2">
              <label class="form-label small fw-semibold text-muted">Trip</label>
              <select class="form-select" [ngModel]="attRecTrip()" (ngModelChange)="attRecTrip.set($event)">
                <option value="all">Both trips</option>
                <option value="Morning">Morning</option>
                <option value="Afternoon">Afternoon</option>
              </select>
            </div>
            <div class="col-md-2">
              <label class="form-label small fw-semibold text-muted">Status</label>
              <select class="form-select" [ngModel]="attRecStatus()" (ngModelChange)="attRecStatus.set($event)">
                <option value="">All</option>
                <option value="Boarded">Used transport</option>
                <option value="Not Boarded">Not boarded</option>
                <option value="Leave">On leave</option>
              </select>
            </div>
            <div class="col-md-3">
              <label class="form-label small fw-semibold text-muted">Search</label>
              <div class="input-group">
                <span class="input-group-text bg-light border-end-0"><i class="bi bi-search text-muted"></i></span>
                <input class="form-control border-start-0" placeholder="Name, ID, class, stop…"
                  [ngModel]="attRecSearch()" (ngModelChange)="attRecSearch.set($event)">
                @if (attRecSearch()) {
                  <button class="btn btn-outline-secondary" (click)="attRecSearch.set('')"><i class="bi bi-x"></i></button>
                }
              </div>
            </div>
            <!-- <div class="col-auto">
              <button class="btn btn-outline-secondary" (click)="resetAttRecordFilters()">
                <i class="bi bi-arrow-counterclockwise me-1"></i>Reset
              </button>
            </div> -->
          </div>
          <!-- <small class="text-muted mt-3 d-block"> -->
          <div class="d-flex flex-wrap gap-2 mt-3 align-items-center">
            {{ attRecDate() ? formatDate(attRecDate()) + ' · ' + getDay(attRecDate()) : 'All dates' }}
            · {{ filteredAttRecords().length }} record(s)
          </div>
          <!-- </small> -->
        </div>
      </div>

      <!-- <div class="att-mark-stats mb-3">
        <div class="ams-item"><span class="ams-val">{{ attRecordSummary().total }}</span><span class="ams-lbl">Records</span></div>
        <div class="ams-item ams-ok"><span class="ams-val">{{ attRecordSummary().boarded }}</span><span class="ams-lbl">Used transport</span></div>
        <div class="ams-item ams-bad"><span class="ams-val">{{ attRecordSummary().notBoarded }}</span><span class="ams-lbl">Not boarded</span></div>
        <div class="ams-item ams-leave"><span class="ams-val">{{ attRecordSummary().onLeave }}</span><span class="ams-lbl">On leave</span></div>
        <div class="ams-item"><span class="ams-val">{{ attRecordSummary().morningBoarded }}</span><span class="ams-lbl">Morning</span></div>
        <div class="ams-item"><span class="ams-val">{{ attRecordSummary().afternoonBoarded }}</span><span class="ams-lbl">Afternoon</span></div>
      </div> -->

      <div class="tr-card">
        <div class="trc-header">
          <i class="bi bi-people-fill me-2"></i>
          Students who used transport
          @if (attRecDate()) { <span class="ms-1 fw-normal">on {{ formatDate(attRecDate()) }}</span> }
        </div>
        <div class="trc-body p-0">
          @if (filteredAttRecords().length === 0) {
            <div class="empty-state"><i class="bi bi-clipboard-x"></i><p>No attendance records match these filters</p></div>
          } @else {
            <div class="table-responsive">
              <table class="alloc-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Student</th>
                    <th>Class</th>
                    <th>Route</th>
                    <th>Stop</th>
                    <th class="text-center">Morning</th>
                    <th class="text-center">Afternoon</th>
                    <th>Used?</th>
                    <th>Remark</th>
                    <th>Marked by</th>
                  </tr>
                </thead>
                <tbody>
                  @for (r of filteredAttRecords(); track r.id) {
                    <tr [class.row-used]="usedTransport(r)" [class.row-missed]="!usedTransport(r) && r.morning !== 'Leave' && r.afternoon !== 'Leave'">
                      <td class="small fw-semibold">{{ formatDate(r.date) }}<div class="text-muted fw-normal">{{ getDay(r.date) }}</div></td>
                      <td>
                        <div class="d-flex align-items-center gap-2">
                          <div class="alloc-avatar">{{ r.student_name.charAt(0) }}</div>
                          <div>
                            <div class="fw-semibold small">{{ r.student_name }}</div>
                            <div class="small text-muted">{{ r.student_id }} · {{ r.roll_no }}</div>
                          </div>
                        </div>
                      </td>
                      <td class="small text-secondary">{{ r.class_name }}</td>
                      <td class="small fw-semibold">{{ getRouteName(r.route_id) }}</td>
                      <td class="small text-muted">{{ r.stop_name }}</td>
                      <td class="text-center"><span class="{{ getBoardingClass(r.morning) }}">{{ r.morning ?? '—' }}</span></td>
                      <td class="text-center"><span class="{{ getBoardingClass(r.afternoon) }}">{{ r.afternoon ?? '—' }}</span></td>
                      <td>
                        @if (usedTransport(r)) {
                          <span class="bd-boarded">Yes</span>
                        } @else if (r.morning === 'Leave' || r.afternoon === 'Leave') {
                          <span class="bd-leave">Leave</span>
                        } @else {
                          <span class="bd-missed">No</span>
                        }
                      </td>
                      <td class="small text-muted">{{ r.remark || '—' }}</td>
                      <td class="small text-secondary">{{ r.marked_by }}</td>
                    </tr>
                  }
                </tbody>
              </table>
            </div>
          }
        </div>
      </div>
    }
  }

</div>
